import { Crop } from 'react-image-crop';
import { DropEvent, useDropzone } from 'react-dropzone';
import { FileWithPreview, core } from '@tock/coretypes';
import { Form, FormikProps } from 'formik';
import { FormTextField } from 'ui';
import { IconCamera } from '@tock/icons';
import { IconMigrationUtil } from 'omakase/utils/IconMigrationUtil';
import { ImageState } from 'components/profile/EditProfileModal';
import {
  PreferenceOptionMapsByType,
  PreferenceSelectOptionGroupsByType,
} from 'selectors/preferencesSelector';
import { Typography } from 'omakase';
import {
  correctImageFile,
  correctImageOrientationWithCallback,
} from '@tock/shared/utils/orientation';
import { isDefined } from 'utils/arrayUtils';
import { useEffect, useState } from 'react';
import CircleImage from 'components/profile/CircleImage';
import DestinationEditor from 'components/profile/DestinationEditor';
import ImageCropModal from 'components/modal/ImageCropModal';
import ImportantDateEditor from 'components/profile/ImportantDateEditor';
import LoadingComponent from '@tock/shared/components/LoadingComponent';
import LocationSuggestField from 'components/inputs/LocationSuggestField';
import SelectedOptionsList from 'components/profile/SelectedOptionsList';
import SvgIconCamera from 'components/icons/svgr/SvgIconCamera';
import _ from 'lodash';

// iPhone X file sizes are ~5MB
const maxFileSize = 8388608; // ~8MB;

export interface EditProfileTimeFormFields {
  aversions: string;
  cuisines?: core.IPreferenceOption[];
  hospitality?: core.IPreferenceOption[];
  days: core.IPatronProfileDay[];
  destinations: core.IMetroArea[];
  restrictions?: core.IPreferenceOption[];
  imageField: string | undefined;
  place?: google.maps.places.AutocompletePrediction;
}

interface Props {
  allPreferenceSelectOptions: PreferenceSelectOptionGroupsByType;
  allPreferencesByIdByType: PreferenceOptionMapsByType;
  formikProps: FormikProps<EditProfileTimeFormFields>;
  onImageChanged(imageData: ImageState): void;
  onShowError(errorMessage: string): void;
  onSetIsDirty(isDirty: boolean): void;
}

const getNewKeys = (changedOption: SelectOption, prevSelectedKeys: number[]) => {
  const selectedItemIndex = _.findIndex(
    prevSelectedKeys,
    (selectedOptionKey) => selectedOptionKey === changedOption.key
  );
  let newOptionKeys;
  if (selectedItemIndex === -1) {
    newOptionKeys = [...prevSelectedKeys, changedOption.key];
  } else {
    newOptionKeys = _.clone(prevSelectedKeys);
    newOptionKeys.splice(selectedItemIndex, 1);
  }
  return newOptionKeys;
};

const EditProfileTimeFormInteral = ({
  allPreferencesByIdByType,
  allPreferenceSelectOptions: {
    cuisineOptions: cuisineSelectOptions,
    dietaryRestrictionOptions: dietaryRestrictionSelectOptions,
    hospitalityOptions: hospitalitySelectOptions,
  },
  formikProps,
  onImageChanged,
  onShowError,
  onSetIsDirty,
}: Props) => {
  const [imageToCrop, setImageToCrop] = useState<File>();
  const [isImageLoading, setIsImageLoading] = useState(false);

  useEffect(() => {
    onSetIsDirty(formikProps.dirty);
  }, [formikProps.dirty, onSetIsDirty]);

  const onChangePatronImage = (file: FileWithPreview, crop?: Crop) => {
    correctImageOrientationWithCallback(
      file,
      {
        canvas: true,
        crop: true,
        top: crop?.y,
        left: crop?.x,
        sourceHeight: crop?.height,
        sourceWidth: crop?.width,
        orientation: true,
        meta: true,
      },
      (image) => {
        const preview = (image as HTMLCanvasElement).toDataURL();
        formikProps.setFieldValue('imageField', preview);
      },
      (data) => {
        const orientation = data.exif ? (data.exif.get('Orientation') as number) : undefined;
        if (onImageChanged) {
          onImageChanged({ file, orientation, crop });
        }
      }
    );

    setImageToCrop(undefined);
  };

  const onRejectPatronImage = () => {
    onShowError(
      'The file is too large or the wrong format (.jpeg or .png), please choose another file'
    );
  };

  const onSetImageToCrop = async (files: FileWithPreview[], event: DropEvent) => {
    const file = files[0];
    setIsImageLoading(true);

    try {
      const correctedFile = await correctImageFile(file);
      setImageToCrop(correctedFile);
    } catch (e) {
      onShowError(e as string);
    } finally {
      setIsImageLoading(false);
    }
  };

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    accept: 'image/jpeg, image/png',
    onDropAccepted: onSetImageToCrop,
    onDropRejected: onRejectPatronImage,
    maxSize: maxFileSize,
  });

  const handleCloseCropModal = () => {
    setImageToCrop(undefined);
  };

  const selectedCuisineOptionKeys =
    formikProps.values.cuisines?.map((option) => option.id).filter(isDefined) || [];
  const selectedHospitalityOptionKeys =
    formikProps.values.hospitality?.map((option) => option.id).filter(isDefined) || [];
  const selectedRestrictionOptionKeys =
    formikProps.values.restrictions?.map((option) => option.id).filter(isDefined) || [];
  return (
    <LoadingComponent isLoading={isImageLoading} isBallPulse>
      <Form className="EditProfile-form">
        <div className="EditProfile-patronImage-container">
          <div
            {...getRootProps({
              // preventing "Warning: Received `false` for a non-boolean attribute `active`."
              active: isDragActive || undefined,
              size: 'small',
              className: 'EditProfile-patronImage-dropZone',
            })}
            role="button"
            tab-index={0}
            aria-label="Edit profile picture"
          >
            <CircleImage
              className="EditProfile-patronImage"
              imageUrl={formikProps.values.imageField}
              onClick={_.noop}
              alt="Profile image"
            />
            <div className="EditProfile-patronImage-edit-wrapper">
              <IconMigrationUtil
                legacyIcon={<SvgIconCamera className="EditProfile-patronImage-edit" />}
                newIcon={<IconCamera className="EditProfile-patronImage-edit" />}
              />
            </div>
            <input {...getInputProps()} aria-label="Profile image" />
          </div>
        </div>
        <div className="EditProfile-location">
          <LocationSuggestField
            label="Your location"
            onChange={(place: google.maps.places.AutocompletePrediction) =>
              formikProps.setFieldValue('place', place)
            }
            selectedPlace={formikProps.values.place}
            types={['(regions)']}
          />
        </div>
        <div className="SectionDivider" />
        <div className="EditProfile-importantDates">
          <h2 className="ImportantDates-title">
            <Typography color="inherit" component="span" variant="body02Strong">
              Your special days
            </Typography>
          </h2>
          <ImportantDateEditor
            dayValues={formikProps.values.days}
            onChange={(days) => formikProps.setFieldValue('days', days)}
          />
        </div>
        <div className="SectionDivider" />
        <div className="EditProfile-preference">
          <h2>
            <Typography color="inherit" component="span" variant="body02Strong">
              Your favorite cuisines
            </Typography>
          </h2>
          <SelectedOptionsList
            inputLabel="Choose favorite cuisines"
            selectedOptionKeys={selectedCuisineOptionKeys}
            allOptionGroups={cuisineSelectOptions}
            id="favorite-cuisines"
            onChange={(changedOption: SelectOption) => {
              formikProps.setFieldValue(
                'cuisines',
                _.map(
                  getNewKeys(changedOption, selectedCuisineOptionKeys),
                  (selectedOptionKey) => allPreferencesByIdByType.cuisineOptions[selectedOptionKey]
                )
              );
            }}
          />
        </div>
        <div className="EditProfile-preference">
          <h2>
            <Typography color="inherit" component="span" variant="body02Strong">
              Your hospitality preferences
            </Typography>
          </h2>
          <SelectedOptionsList
            inputLabel="Choose hospitality preferences"
            selectedOptionKeys={selectedHospitalityOptionKeys}
            allOptionGroups={hospitalitySelectOptions}
            id="hospitality-preferences"
            onChange={(changedOption: SelectOption) => {
              formikProps.setFieldValue(
                'hospitality',
                _.map(
                  getNewKeys(changedOption, selectedHospitalityOptionKeys),
                  (selectedOptionKey) =>
                    allPreferencesByIdByType.hospitalityOptions[selectedOptionKey]
                )
              );
            }}
          />
        </div>
        <div className="EditProfile-preference">
          <h2>
            <Typography color="inherit" component="span" variant="body02Strong">
              Your dietary restrictions
            </Typography>
          </h2>
          <SelectedOptionsList
            inputLabel="Choose dietary restrictions"
            selectedOptionKeys={selectedRestrictionOptionKeys}
            allOptionGroups={dietaryRestrictionSelectOptions}
            id="dietary-restrictions"
            onChange={(changedOption: SelectOption) => {
              let newOptionKeys;
              const changedPreferenceOption =
                allPreferencesByIdByType.dietaryRestrictionOptions[changedOption.key];
              let selectedRestrictionOptionKeysInternal = selectedRestrictionOptionKeys;
              if (changedPreferenceOption.isExclusive) {
                newOptionKeys = [changedOption.key];
              } else {
                const selectedOptionsContainsExclusive =
                  selectedRestrictionOptionKeys.findIndex(
                    (optionKey) =>
                      allPreferencesByIdByType.dietaryRestrictionOptions[optionKey].isExclusive
                  ) !== -1;

                if (selectedOptionsContainsExclusive) {
                  selectedRestrictionOptionKeysInternal = [];
                }
                newOptionKeys = getNewKeys(changedOption, selectedRestrictionOptionKeysInternal);
              }
              formikProps.setFieldValue(
                'restrictions',
                _.map(
                  newOptionKeys,
                  (selectedOption) =>
                    allPreferencesByIdByType.dietaryRestrictionOptions[selectedOption]
                )
              );
            }}
          />
          <Typography component="span" variant="caption">
            Please also confirm dietary restrictions directly with restaurants.
          </Typography>
        </div>
        <div className="EditProfile-preference">
          <h2>
            <Typography color="inherit" component="span" variant="body02Strong">
              Additional dietary notes
            </Typography>
          </h2>
          <Typography color="inherit" component="span" variant="body02">
            What other aversions, restrictions, or notes would you add?
          </Typography>
          <FormTextField
            fullWidth
            inputProps={{
              'aria-label': 'What other aversions, restrictions, or notes would you add?',
            }}
            className="EditProfile-aversions-field"
            id="aversions"
            name="aversions"
            placeholder="Your notes"
            multiline
            rows={3}
          />
        </div>
        <div className="SectionDivider" />
        <div className="EditProfile-destinations">
          <h2>
            <Typography color="inherit" component="span" variant="body02Strong">
              Cities you're following
            </Typography>
          </h2>
          <Typography color="inherit" component="span" variant="body02">
            Add your favorite food cities or dream destinations.
          </Typography>
          <DestinationEditor
            cityValues={formikProps.values.destinations}
            onChange={(metroAreas: core.IMetroArea[]) =>
              formikProps.setFieldValue('destinations', metroAreas)
            }
          />
        </div>
      </Form>
      {imageToCrop && (
        <ImageCropModal
          aspect={1}
          onClose={handleCloseCropModal}
          onShowError={onShowError}
          onSubmit={onChangePatronImage}
          file={imageToCrop}
          submitting={false}
          circularCrop={true}
        />
      )}
    </LoadingComponent>
  );
};

export default EditProfileTimeFormInteral;
